import AttachmentIcon from "@atlaskit/icon/core/attachment";
import CrossIcon from "@atlaskit/icon/core/cross";
import DownloadIcon from "@atlaskit/icon/core/download";
import VideoIcon from "@atlaskit/icon/core/video";
import {token} from "@atlaskit/tokens";
import {useQuery} from "@tanstack/react-query";
import {type ReactNode, useMemo} from "react";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import type {BtlActivityGalleryItem} from "~/domainModels/btlActivities";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getAssetById} from "~/utilities/apis/api";
import {formatDateTime} from "~/utilities/utilities";

type GalleryKind = "image" | "video" | "file";

function galleryItemKind(fileName: string): GalleryKind {
    const lower = fileName.toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(lower)) {
        return "image";
    }
    if (/\.(mp4|webm|mov|mkv|m4v)$/i.test(lower)) {
        return "video";
    }
    return "file";
}

function useWorkspaceAssetBlob({
    workspaceId,
    assetId,
    enabled,
}: {
    workspaceId: Uuid;
    assetId: Uuid;
    enabled: boolean;
}) {
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    return useQuery({
        queryKey: ["workspace-asset-blob", workspaceId, assetId],
        queryFn: async () => {
            const result = await getAssetById({
                workspaceId,
                assetId,
                intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (result.success === false) {
                throw result.error;
            }
            return result.data;
        },
        enabled: enabled === true && assetId !== ("" as Uuid),
        staleTime: 1000 * 60 * 30,
    });
}

export function GalleryItemPreviewTile({
    item,
    onRemove,
}: {
    item: BtlActivityGalleryItem;
    onRemove?: () => void;
}) {
    const kind = useMemo(() => galleryItemKind(item.fileName), [item.fileName]);
    const {workspace} = useWorkspace();
    const {data: blob, isLoading, isError} = useWorkspaceAssetBlob({
        workspaceId: workspace.id,
        assetId: item.assetId,
        enabled: true,
    });
    const objectUrl = useMemo(() => {
        if (blob === undefined) {
            return null;
        }
        return URL.createObjectURL(blob);
    }, [blob]);

    let preview: ReactNode;
    if (isLoading === true) {
        preview = (
            <div
                className="grid h-[70px] w-[164px] place-items-center"
                style={{background: token("color.background.neutral.subtle")}}
            >
                <AnankeText size="small" color="color.text.subtlest">...</AnankeText>
            </div>
        );
    } else if (isError === true || objectUrl === null) {
        preview = (
            <div
                className="grid h-[70px] w-[164px] place-items-center"
                style={{background: token("color.background.neutral.subtle")}}
            >
                {kind === "video" ? (
                    <VideoIcon label="" color={token("color.icon.subtle")} />
                ) : (
                    <AttachmentIcon label="" color={token("color.icon.subtle")} />
                )}
            </div>
        );
    } else if (kind === "image") {
        preview = <img alt="" src={objectUrl} className="h-full w-full object-cover" />;
    } else if (kind === "video") {
        preview = (
            <>
                <video src={objectUrl} className="h-full w-full object-cover" muted playsInline />
                <div
                    className="absolute inset-0 grid place-items-center pointer-events-none"
                    style={{background: "rgba(0, 0, 0, 0.25)"}}
                >
                    <VideoIcon label="" color={token("color.icon.inverse")} />
                </div>
            </>
        );
    } else {
        preview = (
            <div
                className="grid h-full w-full place-items-center"
                style={{background: token("color.background.neutral.subtle")}}
            >
                <AttachmentIcon label="" color={token("color.icon.subtle")} />
            </div>
        );
    }

    return (
        <div
            className="relative grid overflow-hidden border border-ds-border rounded-[4px] bg-ds-background-input"
            style={{
                width: "164px",
                height: "114px",
                maxWidth: "752px",
                maxHeight: "114px",
                gridTemplateRows: "70px 44px",
                borderColor: token("color.border"),
                background: token("color.background.input"),
                boxShadow: "0 1px 2px 0 rgba(9, 30, 66, 0.15)",
            }}
        >
            <div
                className="relative grid place-items-center overflow-hidden border-b border-ds-border"
                style={{
                    width: "164px",
                    height: "70px",
                    borderColor: token("color.border"),
                }}
            >
                {preview}
            </div>
            <div
                className="grid content-center bg-ds-background-input"
                style={{
                    padding: `${token("space.025", "2px")} ${token("space.100", "8px")}`,
                    background: token("color.background.input"),
                }}
            >
                <span
                    className="w-full truncate"
                    style={{
                        color: token("color.text"),
                        fontFeatureSettings: "'liga' off, 'calt' off",
                        fontFamily: "Inter",
                        fontSize: "var(--Font-Size-Body-S, 12px)",
                        fontStyle: "normal",
                        fontWeight: "var(--Font-Weight-Body-Bold, 653)",
                        lineHeight: "var(--Line-Height-Body-S, 16px)",
                    }}
                >
                    {item.fileName}
                </span>
                <span
                    className="w-full truncate"
                    style={{
                        color: token("color.text"),
                        fontFeatureSettings: "'liga' off, 'calt' off",
                        fontFamily: "Inter",
                        fontSize: "var(--Font-Size-Body-S, 12px)",
                        fontStyle: "normal",
                        fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                        lineHeight: "var(--Line-Height-Body-S, 16px)",
                    }}
                >
                    {formatDateTime(item.uploadedAt)}
                </span>
            </div>
            {onRemove !== undefined || objectUrl !== null ? (
                <div
                    className="absolute right-ds-space-100 top-ds-space-100 grid grid-flow-col auto-cols-max gap-ds-space-050"
                >
                    {onRemove === undefined && objectUrl !== null ? (
                        <div
                            className="grid place-items-center rounded-[4px] border border-ds-border bg-ds-surface"
                            style={{
                                padding: token("space.050"),
                                borderColor: token("color.border"),
                                boxShadow: `0 1px 2px 0 rgba(9, 30, 66, 0.12)`,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    const a = document.createElement("a");
                                    a.href = objectUrl;
                                    a.download = item.fileName;
                                    a.rel = "noopener";
                                    a.click();
                                }}
                                className="grid place-items-center leading-none"
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    color: token("color.icon"),
                                    background: "transparent",
                                }}
                                aria-label="Download"
                            >
                                <DownloadIcon label="" color={token("color.icon")} />
                            </button>
                        </div>
                    ) : null}
                    {onRemove !== undefined ? (
                        <div
                            className="grid place-items-center rounded-[4px] border border-ds-border bg-ds-surface"
                            style={{
                                padding: token("space.050"),
                                borderColor: token("color.border"),
                                boxShadow: `0 1px 2px 0 rgba(9, 30, 66, 0.12)`,
                            }}
                        >
                            <button
                                type="button"
                                onClick={onRemove}
                                className="grid place-items-center leading-none"
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    color: token("color.icon"),
                                    background: "transparent",
                                }}
                                aria-label="Remove attachment"
                            >
                                <CrossIcon label="" color={token("color.icon")} size="small" />
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
